import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../redux/store.ts";
import { setLanguage } from "../redux/slices/languageSlice.ts";

const LanguageSelector = ({ arrowColor = "white" }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const currentLanguage = useAppSelector((state) => state.language.currentLanguage);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { name: t('header.english'), code: "en", flag: "/assets/english.svg" },
    { name: t('header.deutsch'), code: "nl", flag: "/assets/deutch.svg" },
  ];

  const handleLanguageSelect = (langCode) => {
    dispatch(setLanguage(langCode));
    i18n.changeLanguage(langCode);
    setIsDropdownOpen(false);
  };

  // Sync i18n with Redux on mount
  useEffect(() => {
    if (currentLanguage !== i18n.language) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
      >
        <img
          src={languages.find(l => l.code === currentLanguage)?.flag}
          alt={languages.find(l => l.code === currentLanguage)?.name}
          className="w-6 h-6"
        />
        <svg
          width="10"
          height="7"
          viewBox="0 0 10 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transition-transform duration-200 ${
            isDropdownOpen ? "" : "rotate-180"
          }`}
        >
          <path
            d="M3.8301 6.43441C4.23011 6.96736 5.02966 6.96736 5.42967 6.43441L9.05795 1.60028C9.55271 0.941092 9.08237 0 8.25816 0H1.00161C0.177398 0 -0.29294 0.941093 0.20182 1.60028L3.8301 6.43441Z"
            fill={arrowColor}
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-[150px] bg-[#FFFFFF1A] rounded-[10px] p-2 shadow-sm z-50">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageSelect(language.code)}
              className={`w-full flex items-center space-x-2 px-4 py-2 hover:bg-[#FFFFFF33] transition-colors rounded-full ${
                currentLanguage === language.code ? "bg-[#FFFFFF33]" : ""
              }`}
            >
              <img
                src={language.flag}
                alt={language.name}
                className="w-6 h-6"
              />
              <span className={`text-base font-semibold ${arrowColor === "black" ? "text-black" : "text-white"}`}>
                {language.code.toUpperCase()}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;

